import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/esm/Refresh';
import BoardIcon from '@mui/icons-material/esm/ViewKanban';
import TableIcon from '@mui/icons-material/esm/TableChart';
import FilterIcon from '@mui/icons-material/esm/FilterAlt';
import LaunchIcon from '@mui/icons-material/esm/Launch';
import ClearIcon from '@mui/icons-material/esm/Clear';
import SendIcon from '@mui/icons-material/esm/Send';
import HoldIcon from '@mui/icons-material/esm/PauseCircleOutline';
import ReleaseIcon from '@mui/icons-material/esm/PlayCircleOutline';
import GenerateIcon from '@mui/icons-material/esm/AddTask';
import LocationIcon from '@mui/icons-material/esm/LocationOn';
import ExpandMoreIcon from '@mui/icons-material/esm/ExpandMore';
import { AppDispatch, RootState } from '../../store';
import { fetchTasks, setTasksFromCache } from '../../store/slices/tasksSlice';
import { PageHeader } from '../../components/layout';
import { StatusBadge, KPICard } from '../../components/common';
import type { Task } from '../../services/api/tasksService';
import dailyDispatchService, {
  DailyDispatch,
  DailyDispatchPacket,
  DispatchDeliveryChannel,
} from '../../services/api/dailyDispatchService';
import { cachedTasks, mergeById } from '../../services/offline/fieldOfflineForms';

type ViewMode = 'board' | 'matrix';

type DispatchColumn = {
  key: string;
  title: string;
  color: 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
};

const STATUS_COLUMNS: DispatchColumn[] = [
  { key: 'scheduled', title: 'Scheduled', color: 'info' },
  { key: 'waiting', title: 'Waiting', color: 'warning' },
  { key: 'ready', title: 'Ready', color: 'primary' },
  { key: 'blocked', title: 'Blocked', color: 'error' },
  { key: 'in_progress', title: 'In Progress', color: 'secondary' },
  { key: 'inspection_pending', title: 'Inspection Pending', color: 'warning' },
  { key: 'inspection_failed', title: 'Inspection Failed', color: 'error' },
  { key: 'inspection_passed', title: 'Inspection Passed', color: 'success' },
];

const DISPATCH_GROUPS: Array<{ title: string; columns: DispatchColumn[] }> = [
  {
    title: 'Construction',
    columns: STATUS_COLUMNS.filter((column) => !column.key.startsWith('inspection_')),
  },
  {
    title: 'Inspection',
    columns: STATUS_COLUMNS.filter((column) => column.key.startsWith('inspection_')),
  },
];

const FILTER_STATUSES = [
  { value: '', label: 'All statuses' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'waiting', label: 'Waiting' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'ready', label: 'Ready' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'paused', label: 'Paused' },
  { value: 'inspection_pending', label: 'Inspection pending' },
  { value: 'inspection_failed', label: 'Inspection failed' },
  { value: 'inspection_passed', label: 'Inspection passed' },
];

const FILTER_PRIORITIES = [
  { value: '', label: 'All priorities' },
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'URGENT', label: 'Urgent' },
];

const DELIVERY_CHANNELS: Array<{ value: DispatchDeliveryChannel; label: string }> = [
  { value: 'in_app', label: 'In-app' },
  { value: 'whatsapp', label: 'External channel' },
  { value: 'both', label: 'In-app + external' },
];

const deliveryChannelLabel = (value?: DispatchDeliveryChannel | null): string => {
  if (value === 'in_app') return 'In-app';
  if (value === 'both') return 'In-app + external';
  if (value === 'whatsapp') return 'External channel';
  return '-';
};

const todayInputValue = () => new Date().toISOString().slice(0, 10);

const formatDate = (value?: string | null) => {
  if (!value) return 'No due date';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
};

const toDateOnly = (value?: string | Date | null): string | null => {
  if (!value) return null;
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
};

const getTaskPlannedStart = (task: Task): string | null =>
  toDateOnly((task as any).plannedStartDate ?? task.startDate);

const isAheadOfScheduleForDate = (plannedStart: string | null | undefined, dispatchDate: string): boolean =>
  Boolean(plannedStart && plannedStart > dispatchDate);

const getTaskLabel = (task: Task): string =>
  task.taskCode || task.title || (task as any).name || task.id;

const getDispatchItemMetadata = (item: { metadata?: Record<string, unknown> | null }) =>
  (item.metadata || {}) as {
    taskCode?: string;
    taskName?: string;
    plannedStartDate?: string | Date | null;
    aheadOfSchedule?: boolean;
  };

function getTaskLocation(task: Task): string {
  if (task.workLocationRef?.name) return task.workLocationRef.name;
  if (task.workLocation && task.workLocation.trim()) return task.workLocation.trim();
  const unit = task.component?.unit;
  if (unit?.name) return unit.name;
  if (unit?.code) return unit.code;
  if (task.component?.name) return task.component.name;
  if (task.component?.code) return task.component.code;
  return 'Location not set';
}

function TaskCard({ task, onOpen }: { task: Task; onOpen: () => void }) {
  const percent = typeof task.percentComplete === 'number' ? task.percentComplete : 0;
  const status = task.lifecycleStatus || task.status;
  const location = getTaskLocation(task);
  const specCount = task.specifications?.length ?? 0;
  const summaryLine = [
    task.team?.name || 'Unassigned',
    task.dueDate ? `Due ${formatDate(task.dueDate)}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <Card
      onClick={onOpen}
      sx={{
        borderRadius: 1,
        boxShadow: 1,
        cursor: 'pointer',
        '&:hover': { boxShadow: 3 },
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack spacing={1.25}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" noWrap>
              {task.taskCode || task.id}
            </Typography>
            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {task.title}
            </Typography>
          </Box>

          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Progress</Typography>
              <Typography variant="caption" fontWeight={700}>{percent}%</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={percent}
              aria-label={`Task progress ${percent}%`}
              sx={{ height: 6, borderRadius: 99 }}
            />
          </Box>

          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'text.secondary', minWidth: 0 }}>
            <LocationIcon fontSize="inherit" sx={{ fontSize: 16, flexShrink: 0 }} />
            <Typography variant="caption" noWrap title={location}>{location}</Typography>
            {specCount > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                {specCount} spec{specCount === 1 ? '' : 's'}
              </Typography>
            )}
          </Stack>

          <Divider />

          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <StatusBadge status={status} />
            <Typography variant="caption" color="text.secondary" noWrap title={summaryLine}>
              {summaryLine}
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

interface TasksPageProps {
  fieldMode?: boolean;
}

export default function TasksPage({ fieldMode = false }: TasksPageProps) {
  const dispatch = useDispatch() as AppDispatch;
  const navigate = useNavigate();
  const { tasks, loading, error, total } = useSelector((state: RootState) => state.tasks);
  const { activeProjectId, projects } = useSelector((state: RootState) => state.projects);
  const [viewMode, setViewMode] = useState<ViewMode>('board');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [search, setSearch] = useState('');
  const [materialsOnly, setMaterialsOnly] = useState(false);
  const [dispatchDate, setDispatchDate] = useState(todayInputValue());
  const [deliveryChannel, setDeliveryChannel] = useState<DispatchDeliveryChannel>('both');
  const [manualHold, setManualHold] = useState(false);
  const [dispatchNotes, setDispatchNotes] = useState('');
  const [eligibleTasks, setEligibleTasks] = useState<Task[]>([]);
  const [dispatchRows, setDispatchRows] = useState<DailyDispatch[]>([]);
  const [activePacket, setActivePacket] = useState<DailyDispatchPacket | null>(null);
  const [dispatchLoading, setDispatchLoading] = useState(false);
  const [dispatchError, setDispatchError] = useState<string | null>(null);
  const [dispatchSuccess, setDispatchSuccess] = useState<string | null>(null);

  const projectName = projects.find((project) => project.id === activeProjectId)?.name || 'Current project';

  useEffect(() => {
    const filters = {
      projectId: activeProjectId || undefined,
      status: status || undefined,
      priority: priority || undefined,
      search: search || undefined,
    };
    let cancelled = false;
    (async () => {
      if (!fieldMode) {
        dispatch(fetchTasks(filters));
        return;
      }

      const cached = filterTaskRows(await cachedTasks(activeProjectId), status, priority, search);
      if (!navigator.onLine) {
        if (!cancelled) dispatch(setTasksFromCache(cached));
        return;
      }

      const result = await dispatch(fetchTasks(filters));
      if (cancelled) return;
      if ((result as any).meta?.requestStatus === 'fulfilled') {
        const serverRows = ((result as any).payload?.tasks ?? []) as Task[];
        dispatch(setTasksFromCache(filterTaskRows(mergeById(serverRows, cached), status, priority, search)));
      } else {
        dispatch(setTasksFromCache(cached));
      }
    })();
    return () => { cancelled = true; };
  }, [dispatch, activeProjectId, status, priority, search, fieldMode]);

  const loadDispatchPackets = async () => {
    if (!activeProjectId) {
      setEligibleTasks([]);
      setDispatchRows([]);
      setActivePacket(null);
      return;
    }

    try {
      setDispatchLoading(true);
      setDispatchError(null);
      const packets = await dailyDispatchService.listDispatches({
        projectId: activeProjectId,
        dispatchDate,
      });
      const eligible = fieldMode
        ? []
        : await dailyDispatchService.getEligibleTasks({
          projectId: activeProjectId,
          dispatchDate,
          limit: 100,
        });
      setEligibleTasks(eligible);
      setDispatchRows(packets);
    } catch (err: any) {
      setDispatchError(err.response?.data?.message || 'Failed to load dispatch packets');
    } finally {
      setDispatchLoading(false);
    }
  };

  useEffect(() => {
    loadDispatchPackets();
  }, [activeProjectId, dispatchDate, fieldMode]);

  const visibleTasks = useMemo(
    () =>
      tasks.filter((task) => {
        if (materialsOnly && !task.materialsReady) return false;
        return true;
      }),
    [tasks, materialsOnly]
  );

  const groupedTasks = useMemo(() => {
    const groups = new Map<string, Task[]>();
    STATUS_COLUMNS.forEach((column) => groups.set(column.key, []));
    visibleTasks.forEach((task) => {
      const key = (task.lifecycleStatus || task.status || 'scheduled').toLowerCase();
      const bucket = groups.get(key) || groups.get('scheduled');
      bucket?.push(task);
    });
    return groups;
  }, [visibleTasks]);

  const summary = useMemo(() => {
    const ready = visibleTasks.filter((task) => (task.lifecycleStatus || task.status) === 'READY').length;
    const blocked = visibleTasks.filter((task) => (task.lifecycleStatus || task.status) === 'BLOCKED').length;
    const materialsReady = visibleTasks.filter((task) => task.materialsReady).length;
    const inspections = visibleTasks.filter((task) =>
      ['INSPECTION_PENDING', 'INSPECTION_FAILED', 'INSPECTION_PASSED'].includes(
        (task.lifecycleStatus || task.status).toUpperCase()
      )
    ).length;
    return { ready, blocked, materialsReady, inspections };
  }, [visibleTasks]);

  const activeDispatch = activePacket?.dispatch || dispatchRows[0] || null;
  const aheadEligibleTasks = useMemo(
    () => eligibleTasks.filter((task) => isAheadOfScheduleForDate(getTaskPlannedStart(task), dispatchDate)),
    [eligibleTasks, dispatchDate]
  );

  const handleGenerateDispatch = async () => {
    if (!activeProjectId) return;
    try {
      setDispatchLoading(true);
      setDispatchError(null);
      setDispatchSuccess(null);
      const packet = await dailyDispatchService.generateDraft({
        projectId: activeProjectId,
        dispatchDate,
        deliveryChannel,
        manualHold,
        notes: dispatchNotes || undefined,
      });
      setActivePacket(packet);
      setDispatchSuccess(`Generated ${packet.dispatch.title} with ${packet.items.length} task(s).`);
      await loadDispatchPackets();
    } catch (err: any) {
      setDispatchError(err.response?.data?.message || 'Failed to generate daily dispatch');
    } finally {
      setDispatchLoading(false);
    }
  };

  const handleToggleHold = async (row: DailyDispatch) => {
    try {
      setDispatchLoading(true);
      setDispatchError(null);
      setDispatchSuccess(null);
      const next = await dailyDispatchService.setManualHold(row.id, !row.manualHold);
      setDispatchSuccess(next.manualHold ? 'Dispatch placed on hold.' : 'Dispatch released.');
      await loadDispatchPackets();
    } catch (err: any) {
      setDispatchError(err.response?.data?.message || 'Failed to update dispatch hold');
    } finally {
      setDispatchLoading(false);
    }
  };

  const handleSendDispatch = async (row: DailyDispatch) => {
    try {
      setDispatchLoading(true);
      setDispatchError(null);
      setDispatchSuccess(null);
      const packet = await dailyDispatchService.sendDispatch(row.id, deliveryChannel);
      setActivePacket(packet);
      setDispatchSuccess(`Dispatch sent with status ${packet.dispatch.status}.`);
      await loadDispatchPackets();
    } catch (err: any) {
      setDispatchError(err.response?.data?.message || 'Failed to send dispatch');
    } finally {
      setDispatchLoading(false);
    }
  };

  const handleRefresh = () => {
    dispatch(
      fetchTasks({
        projectId: activeProjectId || undefined,
        status: status || undefined,
        priority: priority || undefined,
        search: search || undefined,
      })
    );
  };

  const clearFilters = () => {
    setStatus('');
    setPriority('');
    setSearch('');
    setMaterialsOnly(false);
  };

  return (
    <Box>
      <PageHeader
        title="Dispatch Board"
        subtitle={`Phase 1 dispatch view for ${projectName}`}
        actions={
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} disabled={loading} aria-label="Refresh dispatch board">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={viewMode}
              onChange={(_, nextValue) => nextValue && setViewMode(nextValue)}
            >
              <ToggleButton value="board" aria-label="board view">
                <BoardIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="matrix" aria-label="matrix view">
                <TableIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {dispatchError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {dispatchError}
        </Alert>
      )}
      {dispatchSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {dispatchSuccess}
        </Alert>
      )}

      {!activeProjectId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select a project in the header to load dispatch work.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Visible Tasks" value={visibleTasks.length} color="primary" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Ready" value={summary.ready} color="success" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Blocked" value={summary.blocked} color="error" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Materials Ready" value={summary.materialsReady} color="info" />
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 1 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'stretch', lg: 'center' }}>
            <Box sx={{ minWidth: 220 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Daily Dispatch Packet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {fieldMode
                  ? `Packets for ${formatDate(dispatchDate)}`
                  : `${eligibleTasks.length} eligible task(s) for ${formatDate(dispatchDate)}`}
              </Typography>
            </Box>
            <TextField
              label="Dispatch date"
              type="date"
              size="small"
              value={dispatchDate}
              onChange={(event) => setDispatchDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 180 }}
            />
            {!fieldMode && (
              <>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Delivery</InputLabel>
                  <Select
                    value={deliveryChannel}
                    label="Delivery"
                    inputProps={{ 'aria-label': 'Dispatch delivery channel' }}
                    onChange={(event) => setDeliveryChannel(event.target.value as DispatchDeliveryChannel)}
                  >
                    {DELIVERY_CHANNELS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox
                    checked={manualHold}
                    onChange={(event) => setManualHold(event.target.checked)}
                    inputProps={{ 'aria-label': 'Create dispatch on hold' }}
                  />
                  <Typography variant="body2">Create on hold</Typography>
                </Stack>
                <TextField
                  label="Packet notes"
                  size="small"
                  value={dispatchNotes}
                  onChange={(event) => setDispatchNotes(event.target.value)}
                  sx={{ minWidth: 220, flex: 1 }}
                />
                <Button
                  variant="contained"
                  startIcon={<GenerateIcon />}
                  onClick={handleGenerateDispatch}
                  disabled={!activeProjectId || dispatchLoading || eligibleTasks.length === 0}
                >
                  Generate
                </Button>
              </>
            )}
          </Stack>

          {!fieldMode && eligibleTasks.length > 0 && (
            <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
              <Box sx={{ px: 1.5, py: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Eligible tasks
                </Typography>
                {aheadEligibleTasks.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {aheadEligibleTasks.length} ready task{aheadEligibleTasks.length === 1 ? '' : 's'} planned after this dispatch date.
                  </Typography>
                )}
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Task</TableCell>
                    <TableCell>Planned start</TableCell>
                    <TableCell>Flag</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {eligibleTasks.slice(0, 8).map((task) => {
                    const plannedStart = getTaskPlannedStart(task);
                    const ahead = isAheadOfScheduleForDate(plannedStart, dispatchDate);
                    return (
                      <TableRow key={task.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>
                            {getTaskLabel(task)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {task.title || (task as any).name || ''}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatDate(plannedStart)}</TableCell>
                        <TableCell>
                          {ahead ? (
                            <Chip size="small" color="info" variant="outlined" label="Ahead of schedule" />
                          ) : (
                            <Typography variant="caption" color="text.secondary">On plan / late</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {eligibleTasks.length > 8 && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="caption" color="text.secondary">
                          Showing 8 of {eligibleTasks.length} eligible tasks.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} lg={7}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Packet</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Delivery</TableCell>
                    <TableCell align="right">Items</TableCell>
                    {!fieldMode && <TableCell align="right">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dispatchRows.map((row) => (
                    <TableRow key={row.id} hover selected={activeDispatch?.id === row.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>
                          {row.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.dispatchDate}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={row.status === 'sent' ? 'success' : row.status === 'held' ? 'warning' : 'default'}
                          label={row.status.replace(/_/g, ' ')}
                        />
                      </TableCell>
                      <TableCell>{deliveryChannelLabel(row.deliveryChannel)}</TableCell>
                      <TableCell align="right">{row.deliverySummary?.itemCount ?? '-'}</TableCell>
                      {!fieldMode && (
                        <TableCell align="right">
                          <Tooltip title={row.manualHold ? 'Release hold' : 'Hold dispatch'} describeChild>
                            <span>
                              <IconButton
                                size="small"
                                aria-label={row.manualHold ? 'Release dispatch hold' : 'Hold dispatch'}
                                onClick={() => handleToggleHold(row)}
                                disabled={dispatchLoading || ['sent', 'partially_sent'].includes(row.status)}
                              >
                                {row.manualHold ? <ReleaseIcon fontSize="small" /> : <HoldIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Send dispatch" describeChild>
                            <span>
                              <IconButton
                                size="small"
                                aria-label="Send dispatch"
                                onClick={() => handleSendDispatch(row)}
                                disabled={dispatchLoading || row.manualHold || ['sent', 'partially_sent'].includes(row.status)}
                              >
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {dispatchRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={fieldMode ? 4 : 5}>
                        <Alert severity="info">No dispatch packets have been generated for this date.</Alert>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Grid>
            <Grid item xs={12} lg={5}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Last Send Result
                </Typography>
                {activePacket ? (
                  <Stack spacing={1}>
                    <Typography variant="body2">
                      {activePacket.dispatch.title}: {activePacket.items.length} item(s)
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {activePacket.items.slice(0, 6).map((item) => {
                        const metadata = getDispatchItemMetadata(item);
                        const plannedStart = toDateOnly(metadata.plannedStartDate);
                        return (
                          <Chip
                            key={item.id}
                            size="small"
                            color={metadata.aheadOfSchedule ? 'info' : 'default'}
                            variant="outlined"
                            label={`${item.sequenceOrder}. ${item.deliveryStatus}${metadata.aheadOfSchedule ? ` - Ahead ${formatDate(plannedStart)}` : ''}`}
                          />
                        );
                      })}
                    </Stack>
                    {activePacket.dispatch.deliverySummary && (
                      <Typography variant="caption" color="text.secondary">
                        Sent {activePacket.dispatch.deliverySummary.sentCount || 0}, failed{' '}
                        {activePacket.dispatch.deliverySummary.failedCount || 0}, skipped{' '}
                        {activePacket.dispatch.deliverySummary.skippedCount || 0}
                      </Typography>
                    )}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Send a packet to see task-level delivery statuses here.
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 1 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon fontSize="small" />
            <Typography variant="subtitle1" fontWeight={700}>
              Filters
            </Typography>
          </Box>
          <TextField
            label="Search"
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            fullWidth
            sx={{ maxWidth: 280 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              inputProps={{ 'aria-label': 'Task status filter' }}
              onChange={(event) => setStatus(event.target.value)}
            >
              {FILTER_STATUSES.map((option) => (
                <MenuItem key={option.value || 'all'} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={priority}
              label="Priority"
              inputProps={{ 'aria-label': 'Task priority filter' }}
              onChange={(event) => setPriority(event.target.value)}
            >
              {FILTER_PRIORITIES.map((option) => (
                <MenuItem key={option.value || 'all'} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Checkbox
              checked={materialsOnly}
              onChange={(event) => setMaterialsOnly(event.target.checked)}
              inputProps={{ 'aria-label': 'Materials ready only' }}
            />
            <Typography variant="body2">Materials ready only</Typography>
          </Stack>
          <Button startIcon={<ClearIcon />} onClick={clearFilters}>
            Clear
          </Button>
        </Stack>
      </Paper>

      {loading ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 1 }}>
          <Typography color="text.secondary">Loading dispatch work...</Typography>
        </Paper>
      ) : viewMode === 'board' ? (
        <Stack spacing={2}>
          {DISPATCH_GROUPS.map((group) => {
            const groupCount = group.columns.reduce((sum, column) => sum + (groupedTasks.get(column.key)?.length || 0), 0);
            return (
              <Box key={group.title}>
                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1 }}>
                  <Typography variant="h6" fontWeight={700}>
                    {group.title}
                  </Typography>
                  <Chip size="small" label={groupCount} variant="outlined" />
                </Stack>
                <Stack spacing={1.25}>
                  {group.columns.map((column) => {
                    const columnTasks = groupedTasks.get(column.key) || [];
                    return (
                      <Accordion
                        key={column.key}
                        defaultExpanded={columnTasks.length > 0}
                        disableGutters
                        elevation={1}
                        sx={{
                          borderRadius: 1,
                          borderLeft: 4,
                          borderLeftColor: `${column.color}.main`,
                          '&:before': { display: 'none' },
                          overflow: 'hidden',
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            minHeight: 48,
                            '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center' },
                          }}
                        >
                          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {column.title}
                            </Typography>
                            <Chip size="small" label={columnTasks.length} color={column.color} />
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                          {columnTasks.length === 0 ? (
                            <Box sx={{ py: 2, textAlign: 'center' }}>
                              <Typography variant="body2" color="text.secondary">
                                No tasks in this status.
                              </Typography>
                            </Box>
                          ) : (
                            <Grid container spacing={1.5}>
                              {columnTasks.map((task) => (
                                <Grid key={task.id} item xs={12} sm={6} md={4} lg={3}>
                                  <TaskCard
                                    task={task}
                                    onOpen={() => navigate(`${fieldMode ? '/field/tasks' : '/tasks'}/${task.id}`)}
                                  />
                                </Grid>
                              ))}
                            </Grid>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      ) : (
        <Paper sx={{ borderRadius: 1, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Task</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Team</TableCell>
                <TableCell>Materials</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell align="right">Progress</TableCell>
                <TableCell align="right">Open</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleTasks.map((task) => (
                <TableRow key={task.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>
                      {task.taskCode || task.id}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {task.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <LocationIcon fontSize="inherit" sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">{getTaskLocation(task)}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={task.lifecycleStatus || task.status} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={task.priority} type="priority" />
                  </TableCell>
                  <TableCell>{task.team?.name || 'Unassigned'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={task.materialsReady ? 'success' : 'warning'}
                      variant={task.materialsReady ? 'filled' : 'outlined'}
                      label={task.materialsReady ? 'Ready' : 'Pending'}
                    />
                  </TableCell>
                  <TableCell>{task.paymentStatus || 'N/A'}</TableCell>
                  <TableCell align="right">{task.percentComplete}%</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Open task detail">
                      <IconButton
                        onClick={() => navigate(`${fieldMode ? '/field/tasks' : '/tasks'}/${task.id}`)}
                        size="small"
                        aria-label={`Open task ${task.taskCode || task.title}`}
                      >
                        <LaunchIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {visibleTasks.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Alert severity="info">No dispatch tasks match the current filters.</Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {total} tasks loaded from the current project scope.
        </Typography>
      </Box>
    </Box>
  );
}

function filterTaskRows(rows: Task[], status: string, priority: string, search: string): Task[] {
  const needle = search.trim().toLowerCase();
  return rows.filter((task) => {
    if (status && (task.lifecycleStatus || task.status || '').toLowerCase() !== status.toLowerCase()) return false;
    if (priority && task.priority !== priority) return false;
    if (needle) {
      const haystack = [task.title, task.taskCode, task.description].filter(Boolean).join(' ').toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    return true;
  });
}
